// Pallet desk, screen 2 of 2 — the Confirm Shipment equivalent. One card
// per pallet, the booking line, the delivery address folded away, the
// order's attachments, and one big button:
//
//   API carrier, booked / released:  Print labels (the Palletways PDF —
//                                     the one just booked, or the stored
//                                     one for a reprint). Void stays here
//                                     for a booked, unreleased order.
//   Manual carrier, ready:           tracking fields, then "Print label &
//                                     Ship": prints the attached shipping
//                                     label(s) and ships the order, behind
//                                     a confirm. With nothing attached the
//                                     button reads "Ship" and says so.
//   Manual carrier, shipped:         Print label.
//   Direct carrier (JAS), ready:     "Book with JAS and ship", behind a
//                                     confirm: the BE books the shipment,
//                                     the label prints, the order ships.
//   Direct carrier, shipped:         Print label / Print waybill (the
//                                     documents JAS returned).
//
// Auto print (the dispatch toggle, shared preference) fires the print on
// arrival: the Palletways label for a booked order, the attached label
// for a manual one — never the Ship itself.
//
// Done (and the back arrow) return to the desk's list.
import { useLayoutEffect, useRef, useState } from "react";
import { ArrowLeft, Check, ChevronDown, FileText, MapPin, Package, Printer, Send, Truck } from "lucide-react";
import { useTranslation } from "../../i18n";
import PalletAddress from "./pallet-address";
import PalletAttachments from "./pallet-attachments";
import PalletCarrierChooser from "./pallet-carrier-chooser";
import { carrierOptionsOf, isDirectOrder, isManualOrder, routedRuleName, type DeskBooking, type DeskDelivery, type DeskDocument, type DeskOrder, type DeskState } from "./palletDesk";

export type ConfirmState = DeskState | "shipped";
export type DeskConfirm = "ship" | "void" | null;

/** What Ship answered: the carrier, the tracking numbers and, from a direct
 *  carrier, the documents it returned. */
export interface ShipResult {
    carrier: string;
    tracking_numbers: string[];
    label?: DeskDocument | null;
    waybill?: DeskDocument | null;
    /** Direct carrier: the booking Ship answered with (Raben: reference, planned delivery, eWZ upload). */
    booking?: DeskBooking | null;
}

interface Props {
    order: DeskOrder;
    state: ConfirmState;
    busy: boolean;
    readOnly: boolean;
    autoPrint: boolean;
    /** Set after a Ship: what the order went out on. */
    shipResult: ShipResult | null;
    tracking: string;
    trackingUrl: string;
    onTracking: (v: string) => void;
    onTrackingUrl: (v: string) => void;
    confirm: DeskConfirm;
    onConfirm: (c: DeskConfirm) => void;
    /** Carrier label print (Palletways: fresh booking or reprint; JAS: the stored label). */
    onPrintLabels: () => void;
    /** Direct carrier: print the waybill (consignment note) JAS returned. */
    onPrintWaybill: () => void;
    /** Print the attached shipping label(s) without shipping (auto print on a manual order). */
    onPrintAttachedLabels: () => void;
    /** Manual carrier: print the attached label(s) and ship. Direct carrier: book and ship. */
    onShip: () => void;
    /** Several carriers serve the country: the operator's pick (the order already carries it as `carrier`). */
    onCarrierChange: (code: string) => void;
    onVoid: () => void;
    onDone: () => void;
    /** The delivery address was corrected on the desk (saved already). */
    onDelivery: (delivery: DeskDelivery) => void;
    run: (action: () => Promise<void>) => void;
    fail: (code: string) => void;
    onChanged: () => Promise<void>;
}

const PalletConfirmScreen = ({
    order, state, busy, readOnly, autoPrint, shipResult, tracking, trackingUrl, onTracking, onTrackingUrl,
    confirm, onConfirm, onPrintLabels, onPrintWaybill, onPrintAttachedLabels, onShip, onCarrierChange, onVoid, onDone, onDelivery, run, fail, onChanged,
}: Props) => {
    const { t } = useTranslation();
    const manual = isManualOrder(order);
    const direct = isDirectOrder(order);
    const carrierName = order.carrier?.name ?? "";
    const [addressOpen, setAddressOpen] = useState(false);
    const labelAttachments = (order.attachments ?? []).filter((a) => a.kind === "shipping_label");
    const hasLabel = labelAttachments.length > 0;
    // The carrier's number is required to Ship a manual order (BE tracking_required): the
    // sales channel refuses a shipment without one, and a placeholder ship
    // left 295 Jobalots orders never marked shipped (2026-10-21..28).
    const hasTracking = tracking.trim().length > 0;
    const booked = !manual && !direct && (state === "booked" || state === "released");
    const manualReady = manual && state === "ready" && !!order.carrier;
    const directReady = direct && state === "ready" && !!order.carrier;
    const directShipped = direct && state === "shipped";
    const hasWaybill = !!shipResult?.waybill?.contentBase64 || order.booking?.has_waybill === true;
    // Raben: the delivery window it planned, and a non-blocking note when the eWZ upload failed.
    const shippedBooking = shipResult?.booking ?? order.booking ?? null;
    const planned = shippedBooking?.planned_delivery ?? null;
    const wzFailed = !!shippedBooking?.wz && !shippedBooking.wz.uploaded && !!shippedBooking.wz.error;

    // Auto print once, on arrival — never a Ship.
    const hasAutoTriggered = useRef(false);
    useLayoutEffect(() => {
        if (!autoPrint || hasAutoTriggered.current) return;
        hasAutoTriggered.current = true;
        if (booked) onPrintLabels();
        else if (manual && hasLabel) onPrintAttachedLabels();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const stateLabel = state === "shipped"
        ? t("pallet_dispatch_state_shipped")
        : state === "ready" && (manual || direct) ? t("pallet_dispatch_state_ready_manual") : t(`pallet_dispatch_state_${state}`);
    const chipState = state === "shipped" ? "released" : state;

    return (
        <div className="dispatch-page pallet-screen">
            <div className="page-data page-data--full" style={{ height: "calc(100vh - 56px)", overflow: "auto" }}>
                <div className="page-title-bar">
                    <h1 className="page-title">
                        {t("order_title_label")} #{order.order_number ?? order.order_id}
                        <span className={`pallet-dispatch-chip pallet-dispatch-chip--${chipState}`}>{stateLabel}</span>
                    </h1>
                    <button type="button" className="page-back-btn" onClick={onDone} aria-label={t("back")}>
                        <ArrowLeft size={20} />
                    </button>
                </div>

                <div className="dispatch-ship-summary">
                    {order.pallets.map((p, i) => (
                        <div key={p.sku} className="dispatch-ship-parcel">
                            <div className="dispatch-ship-parcel-icon"><Package size={22} /></div>
                            <div className="dispatch-ship-parcel-info">
                                <p className="dispatch-ship-parcel-carrier">{p.sku}{p.size_name ? ` · ${p.size_name}` : ""}</p>
                                <p className="dispatch-ship-parcel-tracking">
                                    {manual
                                        ? [p.height_cm != null ? `${p.height_cm} cm` : null, p.volumetric_kg != null ? `${p.volumetric_kg} kg` : null].filter(Boolean).join(" • ") || "—"
                                        : direct
                                            ? [p.height_cm != null ? `${p.height_cm} cm` : null, `${p.weight_kg} kg`, shipResult?.tracking_numbers?.[i]].filter(Boolean).join(" • ")
                                            : [p.type, `${p.weight_kg} kg`, order.booking?.barcodes?.[i]].filter(Boolean).join(" • ")}
                                </p>
                            </div>
                            {order.carrier && (
                                <span className="dispatch-ship-badge dispatch-ship-badge--standard pallet-screen-carrier">
                                    <Truck size={16} strokeWidth={2.4} aria-hidden="true" />
                                    {order.carrier.name}
                                </span>
                            )}
                        </div>
                    ))}
                </div>

                {order.booking && booked && (
                    <p className="pallet-dispatch-booking">
                        {t("pallet_dispatch_booking_line", {
                            con: order.booking.con_no ?? "",
                            service: order.booking.service_name ?? "",
                            barcodes: order.booking.barcodes.join(", "),
                        })}
                    </p>
                )}
                {shipResult && (
                    <p className="pallet-dispatch-booking">
                        {t("pallet_dispatch_shipped_line", { carrier: shipResult.carrier, tracking: shipResult.tracking_numbers.join(", ") || "—" })}
                    </p>
                )}
                {planned && (state === "shipped" || direct) && (
                    <p className="pallet-dispatch-booking">
                        {t("pallet_dispatch_planned_delivery", { from: planned.from, to: planned.to })}
                    </p>
                )}
                {wzFailed && (
                    <p className="pallet-dispatch-note pallet-dispatch-note--warn">
                        {t("pallet_dispatch_wz_upload_failed", { error: shippedBooking?.wz?.error ?? "" })}
                    </p>
                )}
                {state === "released" && <p className="pallet-dispatch-note">{t("pallet_dispatch_released_hint")}</p>}
                {routedRuleName(order) && (
                    <p className="pallet-dispatch-note pallet-dispatch-rule-line">{t("pallet_dispatch_routed_by_rule", { rule: routedRuleName(order) ?? "" })}</p>
                )}
                {state === "ready" && (
                    <PalletCarrierChooser options={carrierOptionsOf(order)} value={order.carrier?.code ?? null} onChange={onCarrierChange} disabled={busy || readOnly || confirm === "ship"} />
                )}

                <div className="dispatch-address-card">
                    <button type="button" className="dispatch-address-header" onClick={() => setAddressOpen((v) => !v)} aria-expanded={addressOpen}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--color-text-muted)", fontSize: "0.75rem", fontWeight: 700, textTransform: "uppercase" }}>
                            <MapPin size={14} />
                            {t("delivery_address")}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            {!addressOpen && <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", fontWeight: 600 }}>{order.delivery.postcode}</span>}
                            <ChevronDown size={16} style={{ color: "var(--color-text-muted)", transition: "transform 0.2s", transform: addressOpen ? "rotate(180deg)" : "none" }} />
                        </div>
                    </button>
                    {addressOpen && (
                        <div className="dispatch-address-text">
                            <PalletAddress order={order} editable={state === "ready" && !readOnly && confirm !== "ship"} busy={busy} run={run} onDelivery={onDelivery} onChanged={onChanged} />
                        </div>
                    )}
                </div>

                {manualReady && (
                    <section className="pallet-dispatch-manual" aria-label={t("pallet_dispatch_manual_heading")}>
                        <p className="pallet-dispatch-carrier">
                            <Truck size={16} /> {t("pallet_dispatch_carrier_line", { carrier: order.carrier!.name, country: order.delivery.country ?? "—" })}
                        </p>
                        {order.carrier!.fallback ? (
                            <>
                                <p className="pallet-dispatch-note pallet-dispatch-note--warn">
                                    {t("pallet_dispatch_manual_fallback", { carrier: order.carrier!.name, missing: order.carrier!.fallback.missing.join(", ") })}
                                </p>
                                <p className="pallet-dispatch-note">{t("pallet_dispatch_manual_fallback_hint", { carrier: order.carrier!.name })}</p>
                            </>
                        ) : (
                            <p className="pallet-dispatch-note">{t("pallet_dispatch_manual_hint", { carrier: order.carrier!.name })}</p>
                        )}
                        <div className="pallet-dispatch-tracking">
                            <label>
                                <span>{t("pallet_dispatch_tracking")} *</span>
                                <input type="text" value={tracking} required aria-required="true" disabled={busy || readOnly} onChange={(e) => onTracking(e.target.value)} />
                            </label>
                            <label>
                                <span>{t("pallet_dispatch_tracking_url")}</span>
                                <input type="url" value={trackingUrl} disabled={busy || readOnly} placeholder="https://" onChange={(e) => onTrackingUrl(e.target.value)} />
                            </label>
                        </div>
                        {!hasTracking && <p className="pallet-dispatch-note pallet-dispatch-note--warn">{t("pallet_dispatch_tracking_needed", { carrier: order.carrier!.name })}</p>}
                        {!hasLabel && <p className="pallet-dispatch-note pallet-dispatch-note--warn">{t("pallet_dispatch_no_label_attached")}</p>}
                    </section>
                )}
                {directReady && (
                    <section className="pallet-dispatch-manual" aria-label={t("pallet_dispatch_direct_heading")}>
                        <p className="pallet-dispatch-carrier">
                            <Truck size={16} /> {t("pallet_dispatch_carrier_line", { carrier: carrierName, country: order.delivery.country ?? "—" })}
                        </p>
                        <p className="pallet-dispatch-note">{t("pallet_dispatch_direct_hint", { carrier: carrierName })}</p>
                    </section>
                )}
                {directShipped && hasWaybill && (
                    <p className="pallet-dispatch-note">{t("pallet_dispatch_direct_documents_hint")}</p>
                )}

                <div className="pallet-screen-primary">
                    {booked && (
                        <button type="button" className="btn btn-success btn-xl btn-full" disabled={busy} onClick={onPrintLabels}>
                            <Printer size={24} /> {t(state === "booked" ? "pallet_dispatch_print_labels" : "pallet_dispatch_reprint")}
                        </button>
                    )}
                    {directReady && (confirm !== "ship" ? (
                        <button type="button" className="btn btn-success btn-xl btn-full" disabled={busy || readOnly} onClick={() => onConfirm("ship")}>
                            <Send size={24} /> {t("pallet_dispatch_book_and_ship", { carrier: carrierName })}
                        </button>
                    ) : (
                        <div className="pallet-dispatch-confirm pallet-screen-confirm">
                            <span>{t("pallet_dispatch_direct_confirm_text", { order: order.order_number ?? order.order_id, carrier: carrierName })}</span>
                            <button type="button" className="btn btn-danger" disabled={busy || readOnly} onClick={onShip}>
                                {t("pallet_dispatch_ship_confirm")}
                            </button>
                            <button type="button" className="btn" disabled={busy} onClick={() => onConfirm(null)}>{t("cancel")}</button>
                        </div>
                    ))}
                    {directShipped && (
                        <>
                            <button type="button" className="btn btn-success btn-xl btn-full" disabled={busy} onClick={onPrintLabels}>
                                <Printer size={24} /> {t("pallet_dispatch_print_label")}
                            </button>
                            {hasWaybill && (
                                <button type="button" className="btn btn-secondary btn-xl btn-full" disabled={busy} onClick={onPrintWaybill}>
                                    <FileText size={24} /> {t("pallet_dispatch_print_waybill")}
                                </button>
                            )}
                        </>
                    )}
                    {manualReady && (confirm !== "ship" ? (
                        <button type="button" className="btn btn-success btn-xl btn-full" disabled={busy || readOnly || !hasTracking} onClick={() => onConfirm("ship")}>
                            {hasLabel ? <Printer size={24} /> : <Send size={24} />}
                            {t(hasLabel ? "pallet_dispatch_print_and_ship" : "pallet_dispatch_ship")}
                        </button>
                    ) : (
                        <div className="pallet-dispatch-confirm pallet-screen-confirm">
                            <span>{t("pallet_dispatch_ship_confirm_text", { order: order.order_number ?? order.order_id, carrier: order.carrier!.name })}</span>
                            <button type="button" className="btn btn-danger" disabled={busy || readOnly || !hasTracking} onClick={onShip}>
                                {t("pallet_dispatch_ship_confirm")}
                            </button>
                            <button type="button" className="btn" disabled={busy} onClick={() => onConfirm(null)}>{t("cancel")}</button>
                        </div>
                    ))}
                    {manual && state === "shipped" && hasLabel && (
                        <button type="button" className="btn btn-success btn-xl btn-full" disabled={busy} onClick={onPrintAttachedLabels}>
                            <Printer size={24} /> {t("pallet_dispatch_print_label")}
                        </button>
                    )}
                </div>

                <div className="pallet-dispatch-actions pallet-screen-actions">
                    {state === "booked" && !manual && (confirm !== "void" ? (
                        <button type="button" className="btn btn-danger" disabled={busy || readOnly} onClick={() => onConfirm("void")}>
                            {t("pallet_dispatch_void")}
                        </button>
                    ) : (
                        <span className="pallet-dispatch-confirm">
                            <button type="button" className="btn btn-danger" disabled={busy || readOnly} onClick={onVoid}>
                                {t("pallet_dispatch_void_confirm")}
                            </button>
                            <button type="button" className="btn" disabled={busy} onClick={() => onConfirm(null)}>{t("cancel")}</button>
                        </span>
                    ))}
                    <button type="button" className="btn btn-primary" disabled={busy} onClick={onDone}>
                        <Check size={16} /> {t("pallet_dispatch_done")}
                    </button>
                </div>

                <div className="pallet-screen-attachments">
                    <PalletAttachments order={order} busy={busy} readOnly={readOnly} run={run} fail={fail} onChanged={onChanged} />
                </div>
            </div>
        </div>
    );
};

export default PalletConfirmScreen;
